<template>
    <transition name="benefits-side-panel-fade">
        <div v-if="value" class="benefits-side-panel-backdrop" @mousedown.self="handleBackdropClick">
            <transition name="benefits-side-panel-slide" appear>
                <aside class="benefits-side-panel" :style="{ width }" role="dialog" aria-modal="true"
                    :aria-label="title">
                    <header class="benefits-side-panel-header">
                        <slot name="title">
                            <h2>{{ title }}</h2>
                        </slot>
                    </header>

                    <section class="benefits-side-panel-content">
                        <slot></slot>
                    </section>

                    <footer class="benefits-side-panel-footer">
                        <slot name="footer">
                            <button class="benefits-side-panel-button" type="button" @click="requestClose">
                                {{ closeLabel }}
                            </button>
                        </slot>
                    </footer>
                </aside>
            </transition>
        </div>
    </transition>
</template>

<script>
export default {
    name: "BenefitsSidePanel",
    model: {
        prop: "value",
        event: "input",
    },
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        title: {
            type: String,
            required: true,
        },
        closeLabel: {
            type: String,
            default: "Fechar",
        },
        width: {
            type: String,
            default: "600px",
        },
        closeOnBackdrop: {
            type: Boolean,
            default: true,
        },
        closeOnEscape: {
            type: Boolean,
            default: true,
        },
        appendToBody: {
            type: Boolean,
            default: true,
        },
        beforeClose: {
            type: Function,
            default: null,
        },
    },
    mounted() {
        document.addEventListener("keydown", this.handleEscape);

        if (this.appendToBody) {
            document.body.appendChild(this.$el);
        }
    },
    watch: {
        value(isOpen) {
            document.body.style.overflow = isOpen ? "hidden" : "";
        },
    },
    beforeDestroy() {
        document.removeEventListener("keydown", this.handleEscape);
        document.body.style.overflow = "";

        if (this.appendToBody && this.$el && this.$el.parentNode) {
            this.$el.parentNode.removeChild(this.$el);
        }
    },
    methods: {
        close() {
            this.$emit("input", false);
            this.$emit("close");
        },
        requestClose() {
            if (this.beforeClose && this.beforeClose() === false) return;
            this.close();
        },
        handleBackdropClick() {
            if (this.closeOnBackdrop) {
                this.requestClose();
            }
        },
        handleEscape(event) {
            if (event.key === "Escape" && this.value && this.closeOnEscape) {
                this.requestClose();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.benefits-side-panel-backdrop {
    align-items: stretch;
    backdrop-filter: blur(4px);
    background: rgba(0, 0, 0, 0.5);
    bottom: 0;
    display: flex;
    justify-content: flex-end;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    z-index: 9999;
}

.benefits-side-panel {
    background: #fcfcfd;
    border-left: 1px solid rgba(210, 214, 219, 0.8);
    box-shadow: -24px 0 48px rgba(0, 0, 0, 0.22);
    display: flex;
    flex-direction: column;
    height: 100%;
    max-width: 100vw;
    min-height: 0;
    min-width: 0;
    overflow: hidden;
}

.benefits-side-panel-header {
    padding: 16px 18px 12px;
    text-align: center;

    h2 {
        color: #384250;
        font-family: "Sora", sans-serif;
        font-size: 18px;
        font-weight: 400;
        line-height: 1.3;
        margin: 0;
    }
}

.benefits-side-panel-content {
    flex: 1 1 auto;
    min-height: 0;
    overscroll-behavior: contain;
    overflow-x: hidden;
    overflow-y: auto;
    padding: 0 18px 24px;
}

.benefits-side-panel-footer {
    align-items: center;
    border-top: 1px solid #e5e7eb;
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: space-between;
    padding: 12px;
}

.benefits-side-panel-button {
    align-items: center;
    appearance: none;
    background: #fcfcfd;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    color: #1f2a37;
    cursor: pointer;
    display: inline-flex;
    font-family: "Inter", sans-serif;
    font-size: 14px;
    font-weight: 400;
    height: 36px;
    justify-content: center;
    line-height: 1.2;
    padding: 8px 12px;

    &:hover,
    &:focus {
        background: #f9fafb;
        outline: none;
    }
}

.benefits-side-panel-fade-enter-active,
.benefits-side-panel-fade-leave-active {
    transition: opacity 0.3s ease;
}

.benefits-side-panel-fade-enter,
.benefits-side-panel-fade-leave-to {
    opacity: 0;
}

.benefits-side-panel-slide-enter-active,
.benefits-side-panel-slide-leave-active {
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.benefits-side-panel-slide-enter,
.benefits-side-panel-slide-leave-to {
    transform: translateX(100%);
}

@media (min-width: 1024px) {
    .benefits-side-panel {
        width: 700px !important;
    }
}

@media (max-width: 767px) {
    .benefits-side-panel {
        border-radius: 0;
        height: 100dvh;
        max-width: 100vw;
        width: 100% !important;
    }

    .benefits-side-panel-header {
        padding: 14px 16px 10px;
    }

    .benefits-side-panel-content {
        padding: 0 16px 20px;
    }

    .benefits-side-panel-footer {
        align-items: stretch;
    }

    .benefits-side-panel-footer::v-deep button {
        flex: 1 1 140px;
        min-width: 0;
    }
}
</style>
